<template>
  <template v-if="mobile">
    <RouterLink
      v-for="item in mobileItems"
      :key="item.route.name"
      ref="mobileLinks"
      class="admin-mobile-nav-link"
      :class="{ 'admin-mobile-nav-link-wide': wideMobileItems.includes(item.route.name) || item.route.name === unpairedMobileItem }"
      :data-admin-nav-item="item.route.name"
      :to="item.route"
    >{{ item.label }}</RouterLink>
  </template>
  <aside v-else class="dashboard-side admin-dashboard-navigation">
    <RouterLink class="dashboard-brand" :to="{ name: 'admin-dashboard' }">
      <img src="@/assets/img/icons/RassplFish.png" alt="" />
      <span>Admin</span>
    </RouterLink>
    <nav class="dashboard-nav" aria-label="Admin dashboard">
      <RouterLink v-for="item in items" :key="item.route.name" :to="item.route">
        <component :is="item.icon" :size="19" aria-hidden="true" />
        <span>{{ item.label }}</span>
      </RouterLink>
    </nav>
  </aside>
</template>

<script>
import { adminNavigationItems } from '@/data/adminNavigation';
import { Award, BarChart3, FileCheck2, Fish, Gauge, Handshake, Images, LayoutDashboard, Newspaper, Trophy, Users, UsersRound } from 'lucide-vue-next';

export default {
  name: 'AdminNavigation',
  components: { Award, BarChart3, FileCheck2, Fish, Gauge, Handshake, Images, LayoutDashboard, Newspaper, Trophy, Users, UsersRound },
  props: { mobile: { type: Boolean, default: false } },
  data: () => ({ items: adminNavigationItems, wideMobileItems: [] }),
  computed: {
    unpairedMobileItem() {
      const narrow = this.mobileItems.filter((item) => !this.wideMobileItems.includes(item.route.name));
      return narrow.length % 2 ? narrow.at(-1).route.name : null;
    },
    mobileItems() {
      const remaining = [...this.items];
      const ordered = [];
      const isWide = (item) => this.wideMobileItems.includes(item.route.name);
      while (remaining.length) {
        const item = remaining.shift();
        ordered.push(item);
        if (isWide(item)) continue;
        // Complete a half-width row before placing the next full-width button.
        let partner = remaining.findIndex((candidate) => !isWide(candidate));
        if (partner > 0) {
          const sponsors = remaining.findIndex((candidate) => candidate.route.name === 'admin-sponsors' && !isWide(candidate));
          if (sponsors >= 0) partner = sponsors;
        }
        if (partner >= 0) ordered.push(remaining.splice(partner, 1)[0]);
      }
      return ordered;
    },
  },
  mounted() {
    if (!this.mobile) return;
    const menu = this.$refs.mobileLinks?.[0]?.$el?.parentElement;
    if (!menu) return;
    this.mobileMenuObserver = new ResizeObserver(() => this.updateMobileWidths());
    this.mobileMenuObserver.observe(menu);
    this.updateMobileWidths();
    document.fonts?.ready.then(() => {
      if (this.mobileMenuObserver) this.updateMobileWidths();
    });
  },
  beforeUnmount() {
    this.mobileMenuObserver?.disconnect();
    this.mobileMenuObserver = null;
  },
  methods: {
    updateMobileWidths() {
      const links = this.$refs.mobileLinks || [];
      const menu = links[0]?.$el?.parentElement;
      if (!menu || !menu.clientWidth) return;
      const menuStyle = getComputedStyle(menu);
      const number = (value) => parseFloat(value) || 0;
      const halfWidth = (menu.clientWidth - number(menuStyle.paddingLeft) - number(menuStyle.paddingRight) - number(menuStyle.columnGap)) / 2;
      const context = document.createElement('canvas').getContext('2d');
      if (!context) return;
      const wideItems = [];
      links.forEach((link) => {
        const element = link.$el;
        const style = getComputedStyle(element);
        context.font = style.font || `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        const label = element.textContent.trim();
        const textWidth = context.measureText(label).width + Math.max(0, label.length - 1) * number(style.letterSpacing);
        const spacing = number(style.paddingLeft) + number(style.paddingRight) + number(style.borderLeftWidth) + number(style.borderRightWidth);
        if (textWidth + spacing + 2 > halfWidth) wideItems.push(element.dataset.adminNavItem);
      });
      wideItems.sort();
      if (wideItems.join() !== this.wideMobileItems.join()) this.wideMobileItems = wideItems;
    },
  },
};
</script>

<style scoped>
.admin-dashboard-navigation {
  min-height: 0;
  grid-template-rows: auto minmax(0, 1fr);
}
.dashboard-nav {
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  scrollbar-width: thin;
  scrollbar-color: var(--gold) transparent;
}
</style>
